/* ============================================================
   Trasse Families — "Peter spent KES 7,980 at Rubis. Is that right?"

   The parent's half of a refuel declaration, and the panel is written around
   one rule of tone: the driver is not a suspect. The two answers are "yes,
   that's right" and "I want to ask about this" — never approve/reject, and
   never a word that implies the money was not spent. What a parent queries is
   the amount or the need, not the person's honesty.

   Only pending declarations demand attention. Confirmed ones fall into a quiet
   history below, which is where a household actually wants them: the question
   was answered, the number is in the total, move on.
   ============================================================ */
import React, { useMemo, useState } from 'react';
import { FAM, DISPLAY_FONT } from './familyTheme';
import { FamilyRefuel, needsParent, useFamilyRefuels } from './useFamilyRefuels';

interface Props { orgCode: string }

const Panel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <section style={{
    background: '#fff', border: `1px solid ${FAM.line}`, borderRadius: 20,
    padding: 'clamp(18px,3vw,26px)',
  }}>
    {children}
  </section>
);

function fmtKES(n: number): string {
  return `KES ${Math.round(n).toLocaleString('en-KE')}`;
}

function fmtWhen(d: Date | null): string {
  if (!d) return '';
  return d.toLocaleDateString('en-KE', { weekday: 'short', day: 'numeric', month: 'short' });
}

export default function FamilyRefuelPanel({ orgCode }: Props) {
  const { refuels, loading, confirm, dispute } = useFamilyRefuels(orgCode);
  const [busy, setBusy] = useState('');
  const [queryingId, setQueryingId] = useState('');
  const [note, setNote] = useState('');
  const [err, setErr] = useState('');

  const pending = useMemo(() => refuels.filter(needsParent), [refuels]);
  const settled = useMemo(
    () => refuels.filter((r) => !needsParent(r) && !r.voided).slice(0, 6),
    [refuels],
  );

  const act = async (id: string, fn: () => Promise<void>) => {
    setErr(''); setBusy(id);
    try {
      await fn();
      setQueryingId(''); setNote('');
    } catch {
      setErr('That did not save. Check your connection and try again.');
    } finally {
      setBusy('');
    }
  };

  // Nothing declared and nothing waiting: say nothing at all rather than
  // occupying the screen with an empty box.
  if (loading || (pending.length === 0 && settled.length === 0)) return null;

  return (
    <Panel>
      <h2 style={{ fontFamily: DISPLAY_FONT, fontWeight: 600, fontSize: 30, letterSpacing: '-.01em', margin: 0 }}>
        Fuel
      </h2>
      <p style={{ margin: '10px 0 0', fontSize: 15, lineHeight: 1.6, color: FAM.ink2, maxWidth: 560 }}>
        What your driver says they spent at the pump. Your answer is what puts it into your totals —
        an amount you have not agreed to is never counted as money spent.
      </p>

      {pending.length > 0 && (
        <div style={{ marginTop: 20, display: 'flex', flexDirection: 'column', gap: 10 }}>
          {pending.map((r: FamilyRefuel) => (
            <div key={r.id} style={{
              padding: '15px 17px', borderRadius: 14,
              background: FAM.tint, border: `1px solid ${FAM.tint2}`,
            }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
                <div style={{
                  fontSize: 22, fontWeight: 800, color: FAM.ink,
                  fontVariantNumeric: 'tabular-nums', letterSpacing: '-.01em',
                }}>{fmtKES(r.amountKES)}</div>
                <div style={{ fontSize: 14, color: FAM.ink2, flex: '1 1 auto', minWidth: 0 }}>
                  {r.stationName ? `at ${r.stationName}` : 'at the pump'}
                  {r.declaredAt ? ` · ${fmtWhen(r.declaredAt)}` : ''}
                </div>
              </div>

              {queryingId === r.id ? (
                <div style={{ marginTop: 12 }}>
                  <label style={{
                    fontSize: 10.5, fontWeight: 800, letterSpacing: '.08em',
                    textTransform: 'uppercase', color: FAM.ink3,
                  }}>What do you want to ask?</label>
                  <input
                    value={note}
                    autoFocus
                    onChange={(e) => setNote(e.target.value)}
                    placeholder="The tank was still half full on Friday"
                    style={{
                      font: 'inherit', fontSize: 14, padding: '9px 11px', borderRadius: 10,
                      border: `1px solid ${FAM.line}`, background: '#fff', color: FAM.ink,
                      width: '100%', marginTop: 5,
                    }}
                  />
                  <div style={{ display: 'flex', gap: 9, marginTop: 11, flexWrap: 'wrap' }}>
                    <button
                      type="button"
                      disabled={busy === r.id}
                      onClick={() => act(r.id, () => dispute(r.id, note))}
                      style={{
                        padding: '9px 16px', borderRadius: 10, border: 'none', cursor: 'pointer',
                        background: FAM.amber, color: '#fff', font: 'inherit',
                        fontSize: 13.5, fontWeight: 700,
                      }}
                    >
                      {busy === r.id ? 'Sending…' : 'Send my question'}
                    </button>
                    <button
                      type="button"
                      onClick={() => { setQueryingId(''); setNote(''); }}
                      style={{
                        padding: '9px 15px', borderRadius: 10, border: `1px solid ${FAM.line}`,
                        background: '#fff', color: FAM.ink2, font: 'inherit',
                        fontSize: 13.5, fontWeight: 600, cursor: 'pointer',
                      }}
                    >
                      Back
                    </button>
                  </div>
                </div>
              ) : (
                <div style={{ display: 'flex', gap: 9, marginTop: 13, flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    disabled={busy === r.id}
                    onClick={() => act(r.id, () => confirm(r.id))}
                    style={{
                      padding: '9px 17px', borderRadius: 10, border: 'none',
                      background: busy === r.id ? FAM.accentPale : FAM.accent,
                      color: '#fff', font: 'inherit', fontSize: 13.5, fontWeight: 700,
                      cursor: busy === r.id ? 'not-allowed' : 'pointer',
                    }}
                  >
                    {busy === r.id ? 'Saving…' : "Yes, that's right"}
                  </button>
                  <button
                    type="button"
                    onClick={() => { setQueryingId(r.id); setNote(''); }}
                    style={{
                      padding: '9px 15px', borderRadius: 10, border: `1px solid ${FAM.line}`,
                      background: '#fff', color: FAM.ink2, font: 'inherit',
                      fontSize: 13.5, fontWeight: 600, cursor: 'pointer',
                    }}
                  >
                    I want to ask about this
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {err && (
        <p style={{
          margin: '12px 0 0', padding: '10px 13px', borderRadius: 10,
          background: FAM.dangerTint, color: FAM.danger, fontSize: 13.5,
        }}>{err}</p>
      )}

      {settled.length > 0 && (
        <div style={{ marginTop: pending.length ? 20 : 18 }}>
          <div style={{
            fontSize: 10.5, fontWeight: 800, letterSpacing: '.08em',
            textTransform: 'uppercase', color: FAM.muted, marginBottom: 8,
          }}>Recent</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {settled.map((r) => (
              <div key={r.id} style={{
                display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
                padding: '9px 13px', borderRadius: 10, border: `1px solid ${FAM.line}`,
              }}>
                <span style={{
                  fontSize: 14.5, fontWeight: 700, color: FAM.ink,
                  fontVariantNumeric: 'tabular-nums',
                }}>{fmtKES(r.amountKES)}</span>
                <span style={{ fontSize: 13, color: FAM.ink2, flex: '1 1 auto', minWidth: 0 }}>
                  {r.stationName || 'at the pump'}{r.declaredAt ? ` · ${fmtWhen(r.declaredAt)}` : ''}
                </span>
                <span style={{
                  fontSize: 11, fontWeight: 800, letterSpacing: '.04em',
                  color: r.status === 'confirmed' ? FAM.pos : FAM.amber,
                }}>
                  {r.status === 'confirmed' ? 'AGREED' : 'YOU ASKED'}
                </span>
              </div>
            ))}
          </div>
          {settled.some((r) => r.status === 'disputed') && (
            <p style={{ margin: '10px 0 0', fontSize: 12.5, color: FAM.muted, lineHeight: 1.55 }}>
              A refuel you have questioned stays out of your totals until it is settled — it is not
              deleted, and your driver is not charged for it.
            </p>
          )}
        </div>
      )}
    </Panel>
  );
}
